import { Injectable } from '@angular/core';
import { first, firstValueFrom, lastValueFrom, map, Observable } from 'rxjs';
import { appInjector } from '../app-injector';
import { BulkUpdateType } from '../core-data-model/BulkUpdateApi.connectinterface';
import { DisplayableInTree } from '../core-data-model/DisplayableInTree';
import {
    Folder,
    FolderNameIdentifier,
    FolderResponse,
    FolderUpdate,
    FolderWithAccessDetails
} from '../core-data-model/Folder.model';
import { Job } from '../core-data-model/Job.model';
import {
    OrderedSet,
    StandardMaxOrderedSetSize
} from '../core-data-model/OrderedSet';
import { Person } from '../core-data-model/Person.model';
import { BulkUpdateApi } from '../core-services/BulkUpdateApi.connect';
import { BulkUpdateTransformers } from '../core-services/BulkUpdateApi.datatransform';
import { FoldersApi } from '../core-services/FoldersApi.connect';
import {
    FeatureFlags,
    FeatureFlagsService,
    ModuleAccessService
} from '../system-services';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { Logger } from '../system-services/Logger/Logger.service';
import { UserProfileSettingApi } from '../system-services/UserProfileSettings/UserProfileSetting.connect';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { FoldersApiService } from './folders/folders-api.service';

@Injectable({
    providedIn: 'root'
})
export class FoldersDataService {
    static separator = '\\';
    private readonly PI_VERSION_2 = 2;
    // The subscribale folders is for FolderTreeEntry, which needs to hear about folder children
    // or subchildren changing, despite only having folderId as input, and update accordingly. Look there to see the use case better
    folders: { [name: string]: Folder } = {};
    folderTree: FolderResponse;
    rootFolder: Folder;
    defaultFolder: Folder;
    treeSelection: { item: DisplayableInTree } = null;
    foldersPromise: Promise<Folder[]>;
    defaultFolderId: string = null;

    private mostRecentFolderIds: OrderedSet<string> = new OrderedSet<string>(
        [],
        StandardMaxOrderedSetSize + 1,
        StandardMaxOrderedSetSize
    );

    constructor(
        private legacyFoldersApi: FoldersApi,
        private foldersApiService: FoldersApiService,
        private bulkUpdateApi: BulkUpdateApi,
        private transform: BulkUpdateTransformers,
        private userProfileSettingsService: UserProfileSettingsService,
        private userProfileSettingApi: UserProfileSettingApi,
        private initData: InitDataService,
        private logger: Logger,
        private featureFlagsService: FeatureFlagsService,
        private moduleAccessService: ModuleAccessService
    ) {
        if (
            this.featureFlagsService.getByFlag(FeatureFlags.enableCategories) &&
            (this.initData.account.piVersion === this.PI_VERSION_2 ||
                this.moduleAccessService.hasV2ModuleAccess)
        ) {
            this.fetchFolderTree();
        } else {
            this.fetchFolders().then(() => {
                this.mostRecentFolderIds.orderedArray =
                    this.fetchMostRecentFolders();
            });
        }
    }

    fetchFolders() {
        this.foldersPromise = this.legacyFoldersApi
            .getAllFoldersForCompany(
                this.userProfileSettingsService.currentCompany
            )
            .then((folders: Folder[]) => {
                this.folders = {};
                folders.forEach((folder) => {
                    this.folders[folder.id] = folder;
                });
                this.defaultFolder =
                    this.folders[this.initData.account.folderId];

                this.organizeFoldersIntoTree();
                this.rootFolder = folders.find(
                    (folder) => folder.parentId === null
                );

                this.defaultFolder = this.defaultFolder || this.rootFolder;

                return folders;
            });

        return this.foldersPromise;
    }

    fetchFolderTree() {
        // we need to resolve this promise because it is used in a decorator at the bottom of this file, which is used in other APIs.
        // we should eventually be able to remove it after all companies have been migrated from 1.0 to 2.0
        this.foldersPromise = lastValueFrom(
            this.foldersApiService.getFolderTree().pipe(
                map((root) => {
                    this.rootFolder = this.transformFolderResponse(root);
                    this.folders = this.convertFolderTreetoFolders([
                        this.rootFolder
                    ]);
                    this.defaultFolder = this.rootFolder;
                    return [this.rootFolder];
                })
            )
        );

        return this.foldersPromise;
    }

    getDefaultFolder() {
        return this.getFolder(
            this.userProfileSettingsService.getDefaultFolderId()
        );
    }

    getFolder(folderId: string): Folder {
        return this.folders[folderId];
    }

    getFolderDetails(id: string): Promise<FolderWithAccessDetails> {
        return this.legacyFoldersApi.getFolder(id);
    }

    getRootId(): string {
        return this.rootFolder.id;
    }

    setDefaultFolder(folderId: string): Promise<any> {
        return this.userProfileSettingApi
            .setDefaultFolder(folderId)
            .then(() => {
                this.defaultFolder = this.folders[folderId];
            });
    }
    getDefaultFolderId() {
        return this.defaultFolder.id;
    }

    getPathForFolder(folderId: string): string {
        let folderOfInterest: Folder = this.folders[folderId];
        if (!folderOfInterest) {
            return '';
        }

        let path = folderOfInterest.name;

        while (
            folderOfInterest.parentId &&
            folderOfInterest.parentId.length !== 0
        ) {
            folderOfInterest = this.folders[folderOfInterest.parentId];
            path = `${folderOfInterest.name}${FoldersDataService.separator}${path}`;
        }

        // NOTE: leading slash is required for apis
        return FoldersDataService.separator + path;
    }

    getAllFoldersInPath(folderId: string): Folder[] {
        let workingFolder = this.folders[folderId];
        const returnArray: Folder[] = [this.folders[folderId]];

        while (workingFolder.parentId) {
            returnArray.unshift(this.folders[workingFolder.parentId]);
            workingFolder = this.folders[workingFolder.parentId];
        }
        return returnArray;
    }

    isPotentialCircularReference(
        folderId: string,
        potentialParentId: string
    ): boolean {
        if (folderId === potentialParentId) {
            return true;
        }

        return (
            -1 !==
            this.folders[folderId].childrenIds.findIndex((childId) =>
                this.isPotentialCircularReference(childId, potentialParentId)
            )
        );
    }

    getMostRecentFoldersArray(): Folder[] {
        return (
            this.mostRecentFolderIds
                .getArray()
                // Just in case they deleted a folder since we loaded
                .filter((recentFolderId) => !!this.folders[recentFolderId])
                .map((folderId) => this.folders[folderId])
        );
    }

    findAllWithPartialPathFromFolderSet(
        path: string,
        folderIds: string[] = this.getCompleteFolder().map((f) => f.id)
    ): Folder[] {
        const lowerCasedPath = path.toLocaleLowerCase();

        return folderIds
            .filter((folderId) =>
                this.getPathForFolder(folderId)
                    .toLocaleLowerCase()
                    .includes(lowerCasedPath)
            )
            .map((folderId) => this.folders[folderId]);
    }

    updateFolder(folder: FolderUpdate): Promise<any> {
        return this.legacyFoldersApi.updateFolder(folder);
    }

    updateMostRecentFolders(folderId: string): void {
        this.mostRecentFolderIds.updateArrayTop(folderId);
        this.userProfileSettingsService.updateMostRecentFolders(
            this.mostRecentFolderIds.orderedArray
        );
    }

    async createOrEditFolder(
        folderUpdate: FolderUpdate,
        addList: string[],
        deleteList: string[],
        contentToAdd: (Person | Job)[]
    ) {
        return folderUpdate.originalObject
            ? this.editFolder(folderUpdate, addList, deleteList, contentToAdd)
            : this.createFolder(
                  folderUpdate,
                  addList,
                  deleteList,
                  contentToAdd
              );
    }

    deleteFolder(folder: Folder) {
        return this.legacyFoldersApi.deleteFolder(folder);
    }

    getPathWithAlteredParent(
        folderId: string,
        alteredParentId: string
    ): string {
        const folderOfInterest: Folder = this.folders[folderId];
        if (!folderOfInterest) {
            return '';
        }

        return `${this.getPathForFolder(alteredParentId)}${
            FoldersDataService.separator
        }${folderOfInterest.name}`;
    }

    findFolderInstance(
        identifyFunction: (peopleJobCollectionItem: Folder) => boolean,
        startLookingInFolderId: string = this.rootFolder.id
    ): Folder {
        const startLookingFolder = this.folders[startLookingInFolderId];

        if (identifyFunction(startLookingFolder)) {
            return startLookingFolder;
        }

        const foundId = startLookingFolder.childrenIds.find(
            (childId) => !!this.findFolderInstance(identifyFunction, childId)
        );

        return foundId ? this.folders[foundId] : null;
    }

    async getUncategorizedEmployeesFolder(): Promise<Folder> {
        let uncategorizedEmployeeFolder = null;

        const folderObservable = this.foldersApiService
            .getFolderAndChildren()
            .pipe(
                map((rootFolder: FolderResponse) => {
                    const folderId = rootFolder.children.find(
                        (x) =>
                            x.folderNameIdentifier ===
                            FolderNameIdentifier.UncategorizedEmployees
                    ).folderGuid;
                    return this.getFolder(folderId);
                }),
                first()
            );

        uncategorizedEmployeeFolder =
            await firstValueFrom<Folder>(folderObservable);
        return uncategorizedEmployeeFolder;
    }

    getProspectsFolder(): Observable<Folder> {
        const folderObservable = this.getFolderByNameIdentifier(
            FolderNameIdentifier.Prospects
        );

        return folderObservable;
    }

    private getFolderByNameIdentifier(
        folderNameIdentifier: FolderNameIdentifier
    ): Observable<Folder> {
        return this.foldersApiService.getFolderAndChildren().pipe(
            map((rootFolder: FolderResponse) => {
                const folder = rootFolder.children.find(
                    (x) => x.folderNameIdentifier === folderNameIdentifier
                ).folderGuid;
                return this.getFolder(folder);
            }),
            first()
        );
    }

    private organizeFoldersIntoTree() {
        Object.keys(this.folders).forEach((folderId: string) => {
            const thisFolder = this.folders[folderId];
            if (thisFolder.parentId) {
                this.folders[thisFolder.parentId].children.push(thisFolder);
            }
        });
    }

    private fetchMostRecentFolders(): string[] {
        const serverRecentFolderIdsArray =
            this.userProfileSettingsService.companySpecificSetting.recentlySelectedFolderIds.getArray();
        const foundFolderIds = serverRecentFolderIdsArray.filter(
            (folderId) => !!this.folders[folderId]
        );

        if (serverRecentFolderIdsArray.length !== foundFolderIds.length) {
            this.logger.logError(
                'Unfound <Most Recent Folders>',
                serverRecentFolderIdsArray.filter(
                    (folderId) => !this.folders[folderId]
                )
            );
        }
        return foundFolderIds;
    }

    private getCompleteFolder(): Folder[] {
        return Object.values(this.folders);
    }

    private async editFolder(
        folderUpdate: FolderUpdate,
        addList: string[],
        deleteList: string[],
        contentToAdd: (Person | Job)[]
    ) {
        return Promise.all([
            this.updateFolder(folderUpdate),
            this.runUserModification(
                folderUpdate.originalObject.id,
                addList,
                deleteList
            ),
            this.runContentModification(
                folderUpdate.originalObject.id,
                contentToAdd
            )
        ]);
    }

    private async createFolder(
        folderUpdate: FolderUpdate,
        addList: string[],
        deleteList: string[],
        contentToAdd: (Person | Job)[]
    ) {
        const folder = await this.updateFolder(folderUpdate);
        return Promise.all([
            this.runUserModification(folder.id, addList, deleteList),
            this.runContentModification(folder.id, contentToAdd)
        ]);
    }

    private runUserModification(
        folderId: string,
        addList: string[],
        deleteList: string[]
    ) {
        const promiseList = [];
        if (addList && addList.length > 0) {
            promiseList.push(
                this.legacyFoldersApi.addAccessUsers(folderId, addList)
            );
        }
        if (deleteList && deleteList.length > 0) {
            promiseList.push(
                this.legacyFoldersApi.deleteAccessUsers(folderId, deleteList)
            );
        }
        return Promise.all(promiseList);
    }

    private async runContentModification(
        folderId: string,
        contentToAdd: (Person | Job)[]
    ) {
        if (contentToAdd && contentToAdd.length > 0) {
            return this.bulkUpdateApi.bulkUpdate(
                BulkUpdateType.MoveToFolder,
                this.transform.transformBulkUpdateEntries(contentToAdd),
                folderId
            );
        }
    }

    private transformFolderResponse(folderResponse: FolderResponse): Folder {
        return new Folder({
            id: folderResponse.folderGuid,
            name: folderResponse.folderName,
            parentId: folderResponse.parentFolderGuid,
            folderType: folderResponse.folderType,
            folderNameIdentifier: folderResponse.folderNameIdentifier,
            children: folderResponse.children.map((child) =>
                this.transformFolderResponse(child)
            )
        } as Folder);
    }

    private convertFolderTreetoFolders(treeNodes: Folder[]): {
        [name: string]: Folder;
    } {
        const result = {};

        for (const node of treeNodes) {
            result[node.id] = node;

            const children = node.children;

            if (children.length > 0) {
                Object.assign(
                    result,
                    this.convertFolderTreetoFolders(children)
                );
            }
        }
        return result;
    }
}

export class AsyncFolderDecorator {
    public static waitForFolders() {
        return function (_target: any, _name: string, property: any) {
            const fn = property.value;
            property.value = function (...args: any[]) {
                const folderService = appInjector().get(FoldersDataService);
                return folderService.foldersPromise.then(() =>
                    fn.apply(this, args)
                );
            };
        };
    }
}
